import { CheckCircleTwoTone, CloseCircleTwoTone } from '@ant-design/icons';
import { Button, Form, Result } from 'antd';
import { Store } from 'antd/es/form/interface';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import styled, { RuleSet, css } from 'styled-components';
import { PulseTransition } from '../PulseTransition';
import { Field, OnFinishReturn, OnFinishReturnObject, Trigger } from './SimpleFormTypes';
import { formInputResolver, formRuleResolver } from './formInputResolver';
import {
	canShowField,
	generateCustomGridAreasCss,
	getVisibleGridPositions,
	hasShowWhen,
	isFieldCheckBox,
	isFinishElement,
	sortFields,
} from './utils';

const ExtraWrapper = styled.div`
	padding-top: 0.5em;
`;

const ItemWrapper = styled.div`
	margin-inline-end: 0px;
	width: 100%;
	margin-bottom: 0 !important;

	.ant-form-inline {
		margin-inline-end: 0;
	}
`;

const FormItemStyled = styled(Form.Item)`
	margin-bottom: 0 !important;

	.ant-form-item-label > label {
		padding-bottom: 0;
		color: #595959;
		font-size: 14px;
		line-height: 22px;
	}

	&:first-child {
		width: 100%;
	}

	label {
		width: 100%;
	}

	.ant-form-item-required::before {
		display: none !important;
	}

	.ant-row {
		display: flex;
		flex-direction: column;
	}

	.ant-input-outlined {
		border-radius: 4px;
	}
`;

const SubmitButton = styled(Button)`
	border-radius: 4px;
	font-size: 15px;
	line-height: 22px;
	background: linear-gradient(90deg, #0052d4, #4364f7);
	transition: none;
	font-weight: 500;
	height: 46px;

	&.ant-btn-primary:not(:disabled):not(.ant-btn-disabled):hover {
		background: #0052d4;
	}
`;

const FooterButtons = styled.div<{ $disableMargin: boolean }>`
	width: 100%;
	display: flex;
	justify-content: space-between;
	gap: 10px;
	margin-top: ${({ $disableMargin }) => ($disableMargin ? '0' : '10px')};

	button {
		flex: 1;
	}
`;

const StyledForm = styled(Form)<{ $lessSpace: boolean; $customFieldsPositition?: RuleSet; $inline?: boolean }>`
	gap: ${({ $lessSpace }) => ($lessSpace ? '10px' : '20px')};
	display: grid;
	align-items: end;

	${({ $inline }) =>
		$inline &&
		css`
			gap: 5px;
			display: flex;
			flex-wrap: nowrap;
			align-items: center;

			${FooterButtons} {
			}
		`}

	${({ $customFieldsPositition, $inline }) => {
		if ($inline) {
			return '';
		}

		return $customFieldsPositition
			? css`
					${$customFieldsPositition}
					${FooterButtons} {
						grid-area: footer;
					}
				`
			: css`
					display: grid;
					grid-template-rows: auto;
				`;
	}}
`;

export type PositionMap = Record<string, { row: number; rowIndex: number; len: number }>;
export type StyleMap = Record<string, React.CSSProperties>;

export interface SimpleFormProps {
	hideLabels?: boolean;
	submitButtonTitle?: string;
	fields: Field[];
	initialValues?: Store;
	loading?: boolean;
	customButton?: React.ReactElement;
	disableSubmitButtonIfInitialEqual?: boolean;
	customSubmitDisable?: boolean;
	rules?: Record<string, (changed: unknown) => { key: string; value: unknown }[]>;
	/**
	 * Mapování pozic pro jednotlivé fieldy
	 *
	 * Použití: { "name": { row: 1, rowIndex: 1, len: 50 }} - nastaví field s key "name" na první řádek a první pozici s šířkou 50%
	 */
	positionMap?: PositionMap;
	/**
	 * Triggery pro jednotlivé fieldy
	 *
	 * Použití: { "name": (setFormValues, newValue, allValues) => { setFormValues({ name: newValue }) }} - nastaví hodnotu "name" na newValue
	 * Vhodné když potřebujeme nastavit hodnotu jiného fieldu na základě změny jiného fieldu
	 */
	triggers?: Trigger;
	/**
	 * Povolí loading stav při vykonávání promise onFinish
	 */
	allowFinishFunctionLoading?: boolean;
	/**
	 * Nastaví styl podle "key" property v poli fields
	 *
	 * Použití: { "name": { padding: '0 0.5em 0 0' }} - nastaví padding pro input s key "name"
	 */
	style?: StyleMap;
	prevButtonTitle?: string;
	lessSpaceBetweenItems?: boolean;
	disableSubmitButtonMargin?: boolean;
	/**
	 * Mapa fieldu pro vytvoreni grid-template-areas
	 * priklad:
	 * [['first_name', 'last_name']
	 * ['adress' 'adress']
	 * ['age' 'sex']]
	 * Vytvori pomoci funnce generateCustomGridAreasCss
	 * grid-template-areas:
	 * 		"first_name last_name"
	 * 		"adress     adress"
	 * 	    "age        sex"
	 */
	fieldsGridMap?: string[][];
	inline?: boolean;
	onFinish: (values: any) => OnFinishReturn;
	onChange?: (values: any, changedKeys: string[]) => void;
	onFinishFailed?: (errorInfo: any) => void;
	onClickPrevButton?(): void;

	/**
	 * Pokud je true, tak se všechny fieldy nastaví jako readonly a formulář nejde odeslat
	 */
	isReadonly?: boolean;

	/**
	 * Formulář se automaticky odešla, v případě že se tato hodnota změní
	 */
	sendOnKeyValueChanged?: string;
}

export type SimpleFormRef = {
	resetForm: () => void;
};

export const SimpleForm = forwardRef((props: SimpleFormProps, ref) => {
	const {
		fields,
		submitButtonTitle,
		loading,
		initialValues,
		customButton,
		disableSubmitButtonIfInitialEqual,
		hideLabels = false,
		rules = {},
		positionMap = {},
		triggers = {},
		style = {},
		allowFinishFunctionLoading,
		prevButtonTitle,
		lessSpaceBetweenItems = false,
		disableSubmitButtonMargin = false,
		fieldsGridMap: fieldsGridMapOriginal,
		onChange,
		onFinish,
		onFinishFailed,
		onClickPrevButton,
		isReadonly = false,
		inline = false,
		sendOnKeyValueChanged,
	} = props;

	const [isOnFinishLoading, setIsOnFinishLoading] = useState(false);
	const [finishElement, setFinishElement] = useState<OnFinishReturnObject | undefined>(undefined);
	const [formDone, setFormDone] = useState(false);
	const [disabled, setDisabled] = useState(disableSubmitButtonIfInitialEqual ?? false);
	const [form] = Form.useForm();
	const [, setVersion] = useState(0);

	const forceRerender = () => {
		setVersion(v => v + 1);
	};

	useEffect(() => {
		if (Object.keys(initialValues ?? {}).length > 0) {
			form.setFieldsValue(initialValues);
			forceRerender();
		}
	}, [JSON.stringify(initialValues)]);

	useEffect(() => {
		if (!formDone) {
			forceRerender();
		}
	}, [formDone]);

	const resetForm = () => {
		form.resetFields();
		setFormDone(false);
	};

	useImperativeHandle(ref, () => ({
		resetForm,
	}));

	const onHandleFinish = async (v: any) => {
		setIsOnFinishLoading(true);

		const r = await onFinish(v);
		if (isFinishElement(r)) {
			setFinishElement(r);
			setFormDone(true);
		}

		setIsOnFinishLoading(false);
	};

	const sortedFields = sortFields(fields, positionMap);

	if (finishElement && formDone) {
		return (
			<Result
				status="success"
				icon={
					<PulseTransition>
						{finishElement.status === 'success' ? (
							<CheckCircleTwoTone twoToneColor="#52C41A" style={{ fontSize: '72px' }} />
						) : (
							<CloseCircleTwoTone twoToneColor="#c41a1a" style={{ fontSize: '72px' }} />
						)}
					</PulseTransition>
				}
				title={finishElement.title}
				subTitle={finishElement.subtitle}
				extra={[
					<Button
						type="primary"
						key="extra-first"
						onClick={() => {
							if (finishElement.shouldIgnoreRestartFilters !== true) {
								form.resetFields();
							}
							if (finishElement.actionButtonFn) {
								finishElement.actionButtonFn();
							}
							setFormDone(false);
						}}
						data-testid="form-close-button"
					>
						{finishElement.actionButtonTitle ?? 'Zavřít'}
					</Button>,
				]}
			/>
		);
	}

	const isDisabled = loading || (allowFinishFunctionLoading && isOnFinishLoading);

	const fieldsGridMap = getVisibleGridPositions(fields, form.getFieldsValue(), fieldsGridMapOriginal);

	return (
		<StyledForm
			layout="inline"
			onFinish={onHandleFinish}
			onFinishFailed={onFinishFailed}
			autoComplete="off"
			initialValues={initialValues}
			disabled={isDisabled}
			validateTrigger="onSubmit"
			form={form}
			$lessSpace={lessSpaceBetweenItems}
			$customFieldsPositition={
				fieldsGridMap ? generateCustomGridAreasCss(fieldsGridMap, Boolean(submitButtonTitle)) : undefined
			}
			$inline={inline}
			onValuesChange={(changed, allValues) => {
				const changedKeys = Object.keys(changed);
				const rulesKeys = Object.keys(rules);
				const keys = changedKeys.filter(key => rulesKeys.includes(key));

				if (hasShowWhen(fields, changedKeys)) {
					forceRerender();
				}

				const toSetValues: { name: string; value: any }[] = [];
				keys.forEach(key => {
					const rule = rules[key];
					if (typeof rule === 'function') {
						const value = changed[key];
						const result = rule(value);

						result.forEach((v: { key: string; value: unknown }) => {
							toSetValues.push({ name: v.key, value: v.value });
						});
					}
				});
				let allowChange = true;
				if (toSetValues.length > 0) {
					form.setFields(toSetValues);
					onChange?.(form.getFieldsValue(), changedKeys);
					allowChange = false;
				}

				if (disableSubmitButtonIfInitialEqual) {
					setDisabled(JSON.stringify(allValues) === JSON.stringify(initialValues));
				}
				if (allowChange) {
					onChange?.(allValues, changedKeys);
				}

				changedKeys.forEach(key => {
					if (key in triggers) {
						triggers[key](form.setFieldsValue, changed[key], allValues as any);
					}
				});

				if (sendOnKeyValueChanged && changed[sendOnKeyValueChanged]) {
					onHandleFinish(form.getFieldsValue());
				}
			}}
		>
			{sortedFields.map((field: Field) => {
				if (!canShowField(field, form.getFieldsValue())) {
					return null;
				}

				let label;
				if (!hideLabels && typeof field.label === 'string' && field.label.trim().length > 0) {
					label = `${field.label}${field.required === true ? '' : ' (volitelné)'}`;
				}

				const len = positionMap[field.key]?.len ?? 100;

				const isCheckbox = isFieldCheckBox(field);

				return (
					<ItemWrapper
						key={field.key}
						style={{
							width: `${len}%`,
							...style[field.key],
							gridArea: fieldsGridMap ? field.key.split('#')[0] : undefined,
						}}
					>
						<FormItemStyled
							key={field.key}
							label={label}
							colon={false}
							name={field.key}
							rules={[...(field.rules ?? []), ...formRuleResolver(field)]}
							normalize={(field as any).normalize}
							valuePropName={isCheckbox ? 'checked' : undefined}
							tooltip={field.helper}
							extra={field.extra ? <ExtraWrapper>{field.extra}</ExtraWrapper> : undefined}
						>
							{formInputResolver(
								field,
								{ isReadonly },
								form.getFieldValue(field.key),
								value => form.setFieldValue(field.key, value),
								{ setFormDisabled: setDisabled as any, isFormDisabled: disabled },
							)}
						</FormItemStyled>
						{field.footer && field.footer}
					</ItemWrapper>
				);
			})}
			{submitButtonTitle && !isReadonly && (
				<FooterButtons $disableMargin={disableSubmitButtonMargin}>
					{onClickPrevButton && (
						<Button
							type="default"
							htmlType="button"
							size="large"
							loading={loading}
							disabled={loading || disabled}
							onClick={onClickPrevButton}
							data-testid="form-prev-button"
						>
							{prevButtonTitle ?? 'Zpět'}
						</Button>
					)}
					<SubmitButton
						type="primary"
						htmlType="submit"
						size="large"
						loading={loading || (allowFinishFunctionLoading && isOnFinishLoading)}
						disabled={loading || (allowFinishFunctionLoading && isOnFinishLoading) || disabled}
						data-testid="form-submit-button"
					>
						{submitButtonTitle}
					</SubmitButton>
				</FooterButtons>
			)}
			{customButton && customButton}
		</StyledForm>
	);
});
